import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarCheck,
  Clock,
  AlertCircle,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  QrCode,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Layers,
  History,
  Fingerprint,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import { Html5Qrcode } from "html5-qrcode";
import { toast } from "sonner";
import api from "../../lib/api";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function pad(n) {
  return String(n).padStart(2, "0");
}

function formatTime(isoString) {
  if (!isoString) return "—";
  return new Date(isoString).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function calcDuration(checkIn, checkOut) {
  if (!checkIn) return null;
  const end = checkOut ? new Date(checkOut) : new Date();
  const diff = Math.round((end - new Date(checkIn)) / 60000);
  if (diff < 0) return null;
  if (diff < 60) return `${diff}m`;
  return `${Math.floor(diff / 60)}h ${diff % 60}m`;
}

export default function MemberAttendance() {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [groupedRecords, setGroupedRecords] = useState([]);
  const [rawRecords, setRawRecords] = useState([]);
  const [groupByDay, setGroupByDay] = useState(true);
  const [expandedDays, setExpandedDays] = useState(new Set());
  const [showSelectedDaySessions, setShowSelectedDaySessions] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [scannerOpen, setScannerOpen] = useState(false);
  const [resultDialogOpen, setResultDialogOpen] = useState(false);
  const [resultDialogData, setResultDialogData] = useState({
    loading: false,
    success: false,
    title: "",
    message: "",
    warning: null,
    action: "",
    motivationalMessage: "",
  });
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const getLocalDateString = (d) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const [selectedDate, setSelectedDate] = useState(
    getLocalDateString(new Date()),
  );

  // Calendar navigation
  const today = new Date();
  const [calYear, setCalYear] = useState(today.getFullYear());
  const [calMonth, setCalMonth] = useState(today.getMonth()); // 0-based

  useEffect(() => {
    (async () => {
      const token = localStorage.getItem("member_token");
      if (!token) {
        navigate("/");
        return;
      }
      try {
        const res = await api.get("/member-portal/attendance?groupByDay=true", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const grouped = res.data.attendance || [];
        const raw = res.data.rawAttendance || res.data.attendance || [];
        setGroupedRecords(grouped);
        setRawRecords(raw);
        setRecords(grouped);
      } catch (err) {
        if (err?.response?.status === 401) {
          navigate("/");
          return;
        }
        setError("Failed to load attendance.");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, refreshTrigger]);

  const toggleDayExpanded = (dayId) => {
    setExpandedDays((prev) => {
      const next = new Set(prev);
      if (next.has(dayId)) next.delete(dayId);
      else next.add(dayId);
      return next;
    });
  };

  useEffect(() => {
    if (!scannerOpen) return;

    if (!window.isSecureContext) {
      toast.error(
        "Camera access requires a secure connection (HTTPS or localhost).",
      );
      setScannerOpen(false);
      return;
    }

    let html5QrCode;
    const timer = setTimeout(() => {
      const element = document.getElementById("member-qr-reader");
      if (!element) return;

      html5QrCode = new Html5Qrcode("member-qr-reader");

      const successCallback = async (decodedText) => {
        try {
          await html5QrCode.stop();
        } catch (e) {
          console.error("Failed to stop scanner:", e);
        }
        handleQrScanSuccess(decodedText);
      };

      const errorCallback = () => {};

      // Dynamic qrbox sizing function to ensure a large, responsive square scanner area on all devices
      const qrboxConfig = (viewfinderWidth, viewfinderHeight) => {
        const minEdge = Math.min(viewfinderWidth, viewfinderHeight);
        // Make the scan box 80% of the smaller edge, ensuring it's easy to align the QR code
        const size = Math.floor(minEdge * 0.8);
        return { width: size, height: size };
      };

      const startWithFacingMode = () => {
        html5QrCode
          .start(
            { facingMode: "environment" },
            { fps: 10, qrbox: qrboxConfig },
            successCallback,
            errorCallback,
          )
          .catch((err) => {
            console.warn("Environment camera failed, trying user camera:", err);
            html5QrCode
              .start(
                { facingMode: "user" },
                { fps: 10, qrbox: qrboxConfig },
                successCallback,
                errorCallback,
              )
              .catch((err2) => {
                console.error("Both camera options failed:", err2);
                toast.error(
                  "Failed to access camera. Please check permissions.",
                );
                setScannerOpen(false);
              });
          });
      };

      Html5Qrcode.getCameras()
        .then((devices) => {
          if (devices && devices.length > 0) {
            let cameraId = devices[0].id;
            const backCamera = devices.find(
              (device) =>
                device.label.toLowerCase().includes("back") ||
                device.label.toLowerCase().includes("rear") ||
                device.label.toLowerCase().includes("environment"),
            );
            if (backCamera) {
              cameraId = backCamera.id;
            }
            html5QrCode
              .start(
                cameraId,
                { fps: 10, qrbox: qrboxConfig },
                successCallback,
                errorCallback,
              )
              .catch((err) => {
                console.warn(
                  "Failed to start camera by ID, falling back to facingMode:",
                  err,
                );
                startWithFacingMode();
              });
          } else {
            startWithFacingMode();
          }
        })
        .catch((err) => {
          console.warn(
            "getCameras failed, trying facingMode direct start:",
            err,
          );
          startWithFacingMode();
        });
    }, 500);

    return () => {
      clearTimeout(timer);
      if (html5QrCode && html5QrCode.isScanning) {
        html5QrCode
          .stop()
          .then(() => {
            html5QrCode.clear();
          })
          .catch((err) => console.error("Scanner cleanup error:", err));
      }
    };
  }, [scannerOpen]);

  const handleQrScanSuccess = async (decodedText) => {
    setScannerOpen(false);

    let orgId = "";
    let branchId = "";

    const urlMatch = decodedText.match(
      /\/public\/attendance\/([^/]+)\/([^/]+)/,
    );
    if (urlMatch) {
      orgId = urlMatch[1];
      branchId = urlMatch[2];
    } else {
      const parts = decodedText.split(":");
      if (parts[0] === "ATTENDANCE" && parts.length >= 3) {
        orgId = parts[1];
        branchId = parts[2];
      }
    }

    if (!orgId || !branchId) {
      setResultDialogData({
        loading: false,
        success: false,
        title: "Invalid QR Code",
        message: "Please scan a valid gym check-in QR code.",
        warning: null,
        action: "",
        motivationalMessage: "",
      });
      setResultDialogOpen(true);
      return;
    }

    const member = JSON.parse(localStorage.getItem("member_data") || "{}");
    if (!member?.phone) {
      setResultDialogData({
        loading: false,
        success: false,
        title: "Authentication Error",
        message: "Member details not found. Please login again.",
        warning: null,
        action: "",
        motivationalMessage: "",
      });
      setResultDialogOpen(true);
      return;
    }

    setResultDialogData({
      loading: true,
      success: false,
      title: "Processing Check-in",
      message: "Please wait while we verify your details...",
      warning: null,
      action: "",
      motivationalMessage: "",
    });
    setResultDialogOpen(true);

    try {
      const res = await api.post("/attendance/public-checkin", {
        phone: member.phone,
        organizationId: orgId,
        branchId: branchId,
      });

      setResultDialogData({
        loading: false,
        success: true,
        title: res.data?.action === "checkout" ? "Check-out Successful" : "Check-in Successful",
        message: res.data?.message || "Operation successful!",
        warning: res.data?.warning,
        action: res.data?.action || "",
        motivationalMessage: res.data?.motivationalMessage || "",
      });
      setSelectedDate(getLocalDateString(new Date()));
      setRefreshTrigger((prev) => prev + 1);
    } catch (err) {
      setResultDialogData({
        loading: false,
        success: false,
        title: "Check-in Failed",
        message: err.response?.data?.detail || "An unexpected error occurred during check-in.",
        warning: null,
        action: "",
        motivationalMessage: "",
      });
    }
  };

  if (loading)
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );

  if (error)
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3 text-muted-foreground">
        <AlertCircle className="h-8 w-8" />
        <p>{error}</p>
      </div>
    );

  // Build a Set of attended date strings YYYY-MM-DD for O(1) lookup
  const allDateSources = groupedRecords.length > 0 ? groupedRecords : rawRecords;
  const attendedDates = new Set(
    allDateSources
      .map((r) => {
        const raw = r.date || "";
        return raw.length >= 10 ? raw.slice(0, 10) : "";
      })
      .filter(Boolean),
  );

  // Stats
  const thisMonthKey = `${calYear}-${pad(calMonth + 1)}`;
  const thisMonthDays = allDateSources.filter((r) =>
    (r.date || "").startsWith(thisMonthKey),
  );
  const thisMonthCount = thisMonthDays.length;
  const totalDaysCount = allDateSources.length;
  const totalSessionsCount = rawRecords.length || totalDaysCount;

  // Calendar grid for current calYear/calMonth
  const firstDay = new Date(calYear, calMonth, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
  const cells = Array(firstDay)
    .fill(null)
    .concat(Array.from({ length: daysInMonth }, (_, i) => i + 1));
  // pad to complete rows
  while (cells.length % 7 !== 0) cells.push(null);

  const prevMonth = () => {
    if (calMonth === 0) {
      setCalYear((y) => y - 1);
      setCalMonth(11);
    } else setCalMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (calMonth === 11) {
      setCalYear((y) => y + 1);
      setCalMonth(0);
    } else setCalMonth((m) => m + 1);
  };
  const isToday = (d) =>
    d &&
    calYear === today.getFullYear() &&
    calMonth === today.getMonth() &&
    d === today.getDate();

  // Active records to display
  const displayRecords = groupByDay ? groupedRecords : rawRecords;
  const recent = [...displayRecords].slice(0, 30);

  return (
    <div className="space-y-6 pb-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-black font-heading uppercase tracking-wide">
          Attendance
        </h1>
        <p className="text-muted-foreground text-xs mt-0.5">
          Your gym check-in history and daily workout time
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-3">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-semibold sm:text-xs font-mono uppercase tracking-wider text-muted-foreground mb-0.5 truncate">
                  This Month
                </p>
                <p className="text-base sm:text-xl font-black font-heading line-clamp-2 break-words text-primary">
                  {thisMonthCount}
                </p>
                <p className="text-xs font-semibold sm:text-xs text-muted-foreground mt-0.5 line-clamp-2 break-words ">
                  days attended
                </p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center text-primary">
                <CalendarCheck className="h-5 w-5" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-3">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-semibold sm:text-xs font-mono uppercase tracking-wider text-muted-foreground mb-0.5 truncate">
                  Total
                </p>
                <p className="text-base sm:text-xl font-black font-heading line-clamp-2 break-words text-primary">
                  {totalDaysCount}
                </p>
                <p className="text-xs font-semibold sm:text-xs text-muted-foreground mt-0.5 line-clamp-2 break-words ">
                  days ({totalSessionsCount} punches)
                </p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center text-primary">
                <Clock className="h-5 w-5" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Calendar heatmap */}
      <Card className="bg-card border-border mt-6">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm uppercase tracking-tight font-black">
              {MONTHS[calMonth]} {calYear}
            </CardTitle>
            <div className="flex items-center gap-1">
              <button
                onClick={prevMonth}
                className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={nextMonth}
                className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Day headers */}
          <div className="grid grid-cols-7 mb-1">
            {DAYS.map((d, dIdx) => {
              const isTodayDayOfWeek = dIdx === new Date().getDay();
              return (
                <div
                  key={d}
                  className={`text-center text-xs font-mono uppercase py-1 ${
                    isTodayDayOfWeek
                      ? "text-xs font-mono flex items-center justify-center rounded-md transition-all  bg-foreground text-background font-black shadow-sm"
                      : "text-muted-foreground"
                  }`}
                >
                  {d}
                </div>
              );
            })}
          </div>
          {/* Date cells */}
          <div className="grid grid-cols-7 gap-1">
            {cells.map((day, idx) => {
              if (!day) return <div key={idx} />;
              const dateStr = `${calYear}-${pad(calMonth + 1)}-${pad(day)}`;
              const attended = attendedDates.has(dateStr);
              const isSelected = selectedDate === dateStr;
              const selectedCls = isSelected
                ? "ring-2 ring-primary ring-offset-2 ring-offset-background z-10"
                : "";
              const todayCls = isToday(day)
                ? "border border-primary font-bold"
                : "";
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setSelectedDate(dateStr)}
                  title={attended ? `Attended on ${dateStr}` : dateStr}
                  className={`aspect-square flex items-center justify-center rounded-md text-xs font-medium transition-all cursor-pointer hover:opacity-90 active:scale-95 ${
                    attended
                      ? "bg-emerald-500 text-white font-bold"
                      : "bg-secondary text-muted-foreground"
                  } ${todayCls} ${selectedCls}`}
                >
                  {day}
                </button>
              );
            })}
          </div>
          {/* Legend */}
          <div className="flex items-center gap-3 mt-3 justify-end">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <div className="w-3 h-3 rounded-sm bg-emerald-500" /> Attended
            </div>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <div className="w-3 h-3 rounded-sm bg-secondary" /> Absent
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Selected Day Details */}
      <Card className="bg-card border-border mt-6">
        <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-xs font-semibold sm:text-xs font-mono uppercase tracking-wider text-muted-foreground truncate">
            Day Information
          </CardTitle>
          <span className="text-sm font-semibold text-primary">
            {(() => {
              const [y, m, d] = selectedDate.split("-").map(Number);
              const localDate = new Date(y, m - 1, d);
              return localDate.toLocaleDateString("en-IN", {
                weekday: "short",
                day: "numeric",
                month: "short",
                year: "numeric",
              });
            })()}
          </span>
        </CardHeader>
        <CardContent>
          {(() => {
            const selectedDayRecord = groupedRecords.find(
              (r) => r.date && r.date.slice(0, 10) === selectedDate,
            );
            const selectedRawSessions = rawRecords.filter(
              (r) => r.date && r.date.slice(0, 10) === selectedDate,
            );
            const activeSessions = selectedDayRecord?.sessions || selectedRawSessions;
            const hasDayRecord = Boolean(selectedDayRecord || selectedRawSessions.length > 0);

            if (hasDayRecord) {
              const firstIn = selectedDayRecord?.firstCheckIn || selectedDayRecord?.checkIn || activeSessions[0]?.checkIn;
              const lastOut = selectedDayRecord?.lastCheckOut !== undefined ? selectedDayRecord.lastCheckOut : activeSessions[activeSessions.length - 1]?.checkOut;
              const isCurrentlyActive = selectedDayRecord?.isActive || activeSessions.some((s) => !s.checkOut);
              const totalDurationStr =
                selectedDayRecord?.duration ||
                selectedDayRecord?.totalDuration ||
                calcDuration(firstIn, lastOut);

              return (
                <div className="p-4 rounded-lg bg-primary/5 border border-primary/10 animate-in fade-in duration-200 space-y-3">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                        isCurrentlyActive ? "bg-amber-500/20 text-amber-400 animate-pulse" : "bg-green-500/20 text-green-400"
                      }`}>
                        <CalendarCheck className="h-5 w-5" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-bold text-foreground">
                            {isCurrentlyActive ? "Currently In Gym" : "Present"}
                          </p>
                          {isCurrentlyActive && (
                            <Badge className="bg-amber-500/15 text-amber-400 border-0 text-[10px]">
                              Active Inside
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <p className="text-xs text-muted-foreground">
                            {selectedDayRecord?.branchName || activeSessions[0]?.branchName || "Gym Branch"}
                          </p>
                          {activeSessions.length > 1 && (
                            <button
                              type="button"
                              onClick={() => setShowSelectedDaySessions((prev) => !prev)}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-primary/10 hover:bg-primary/20 text-primary text-[10px] font-mono transition-colors"
                              title="Toggle session details"
                              data-testid="badge-toggle-selected-day-sessions"
                            >
                              <Layers className="h-2.5 w-2.5" />
                              {activeSessions.length} sessions
                              {showSelectedDaySessions ? (
                                <ChevronUp className="h-2.5 w-2.5" />
                              ) : (
                                <ChevronDown className="h-2.5 w-2.5" />
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 sm:gap-6 sm:text-right">
                      {firstIn && (
                        <div>
                          <p className="text-xs text-muted-foreground uppercase tracking-wide">
                            First In
                          </p>
                          <p className="text-base font-mono font-bold text-emerald-500">
                            {formatTime(firstIn)}
                          </p>
                        </div>
                      )}
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">
                          Last Out
                        </p>
                        <p className="text-base font-mono font-bold text-amber-500">
                          {lastOut ? (
                            formatTime(lastOut)
                          ) : (
                            <span className="text-xs font-semibold px-2 py-0.5 rounded bg-amber-500/20 text-amber-400">
                              Active
                            </span>
                          )}
                        </p>
                      </div>
                      {totalDurationStr && (
                        <div>
                          <p className="text-xs text-muted-foreground uppercase tracking-wide">
                            Total Time
                          </p>
                          <Badge variant="secondary" className="text-xs font-mono font-bold text-foreground mt-0.5">
                            {totalDurationStr}
                          </Badge>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Multi-session Breakdown for Selected Day (Togglable) */}
                  {activeSessions.length > 1 && (
                    <div className="pt-2.5 border-t border-border/50">
                      <button
                        type="button"
                        onClick={() => setShowSelectedDaySessions((prev) => !prev)}
                        className="w-full flex items-center justify-between text-[11px] font-mono text-muted-foreground hover:text-foreground transition-colors py-1 cursor-pointer"
                        data-testid="toggle-selected-day-sessions"
                      >
                        <span className="flex items-center gap-1.5 font-medium">
                          <Clock className="h-3.5 w-3.5 text-primary" />
                          Individual Sessions ({activeSessions.length})
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px]">
                            Total: <strong className="text-foreground">{totalDurationStr}</strong>
                          </span>
                          {showSelectedDaySessions ? (
                            <ChevronUp className="h-3.5 w-3.5 text-primary" />
                          ) : (
                            <ChevronDown className="h-3.5 w-3.5 text-primary" />
                          )}
                        </div>
                      </button>
                      {showSelectedDaySessions && (
                        <div className="mt-2 space-y-1.5 animate-in fade-in duration-150">
                          {activeSessions.map((s, sIdx) => (
                            <div
                              key={s.id || sIdx}
                              className="flex items-center justify-between p-2 rounded bg-background/60 border border-border/40 text-xs font-mono"
                            >
                              <div className="flex items-center gap-2">
                                <span className="text-muted-foreground text-[10px]">Session #{sIdx + 1}</span>
                                <span className="text-emerald-500 font-semibold">{formatTime(s.checkIn)}</span>
                                <span className="text-muted-foreground">→</span>
                                {s.checkOut ? (
                                  <span className="text-amber-500 font-semibold">{formatTime(s.checkOut)}</span>
                                ) : (
                                  <Badge className="bg-emerald-500/15 text-emerald-500 border-0 text-[9px] py-0">Active</Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                {(s.duration || calcDuration(s.checkIn, s.checkOut)) && (
                                  <Badge variant="secondary" className="text-[10px] font-mono">
                                    {s.duration || calcDuration(s.checkIn, s.checkOut)}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            } else {
              return (
                <div className="flex items-center gap-3 p-4 rounded-lg bg-secondary/30 border border-border/40 animate-in fade-in duration-200">
                  <div className="w-10 h-10 rounded-full bg-muted/20 flex items-center justify-center text-muted-foreground">
                    <Clock className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-muted-foreground">
                      Absent / No Record
                    </p>
                    <p className="text-xs text-muted-foreground">
                      No attendance was logged on this day.
                    </p>
                  </div>
                </div>
              );
            }
          })()}
        </CardContent>
      </Card>

      {/* Attendance History Section with Daily Summary / All Punches Toggle */}
      <Card className="bg-card border-border mt-6">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <CardTitle className="text-xs font-semibold sm:text-xs font-mono uppercase tracking-wider text-muted-foreground truncate">
                {groupByDay ? "Daily Attendance History" : "All Punch History"}
              </CardTitle>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {groupByDay
                  ? "Consolidated daily visits with session duration"
                  : "All check-in and check-out logs"}
              </p>
            </div>
            {/* View Toggle */}
            <div className="flex items-center bg-secondary/60 rounded-md p-0.5 border border-border self-start sm:self-auto">
              <button
                type="button"
                onClick={() => setGroupByDay(true)}
                className={`px-2.5 py-1 rounded text-[11px] font-heading uppercase tracking-wider transition-all flex items-center gap-1.5 ${
                  groupByDay
                    ? "bg-primary text-primary-foreground font-bold shadow-xs"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid="toggle-grouped-day"
              >
                <Layers className="h-3 w-3" />
                Daily Summary
              </button>
              <button
                type="button"
                onClick={() => setGroupByDay(false)}
                className={`px-2.5 py-1 rounded text-[11px] font-heading uppercase tracking-wider transition-all flex items-center gap-1.5 ${
                  !groupByDay
                    ? "bg-primary text-primary-foreground font-bold shadow-xs"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid="toggle-raw-punches"
              >
                <History className="h-3 w-3" />
                All Punches
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {recent.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 gap-2 text-muted-foreground">
              <CalendarCheck className="h-8 w-8 opacity-40" />
              <p className="text-sm">No attendance records yet</p>
            </div>
          ) : (
            <div className="space-y-0">
              {recent.map((r, i) => {
                const dateObj = r.date ? new Date(r.date) : null;
                const dateLabel = dateObj
                  ? dateObj.toLocaleDateString("en-IN", {
                      weekday: "short",
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })
                  : r.date;

                if (groupByDay) {
                  const hasMultiSessions =
                    (r.sessions && r.sessions.length > 1) || (r.sessionCount && r.sessionCount > 1);
                  const isDayExpanded = expandedDays.has(r.id || r.date);
                  const firstIn = r.firstCheckIn || r.checkIn;
                  const lastOut = r.lastCheckOut;
                  const duration = r.duration || r.totalDuration;

                  return (
                    <div key={r.id || i} className="py-3 border-b border-border last:border-0">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                            <CalendarCheck className="h-4 w-4 text-primary" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium">{dateLabel}</p>
                              {r.isActive && (
                                <Badge className="bg-amber-500/15 text-amber-400 border-0 text-[9px] py-0">
                                  Active
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-0.5">
                              {r.branchName && (
                                <p className="text-xs text-muted-foreground">
                                  {r.branchName}
                                </p>
                              )}
                              {hasMultiSessions ? (
                                <button
                                  type="button"
                                  onClick={() => toggleDayExpanded(r.id || r.date)}
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-primary/10 hover:bg-primary/20 text-primary text-[10px] font-mono transition-colors"
                                  title="View session punches"
                                  data-testid={`expand-sessions-${r.id || i}`}
                                >
                                  <Layers className="h-2.5 w-2.5" />
                                  {r.sessionCount || r.sessions.length} sessions
                                  {isDayExpanded ? (
                                    <ChevronUp className="h-2.5 w-2.5" />
                                  ) : (
                                    <ChevronDown className="h-2.5 w-2.5" />
                                  )}
                                </button>
                              ) : (
                                <span className="text-[10px] font-mono text-muted-foreground">
                                  1 session
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-right flex flex-col items-end gap-0.5">
                          <div className="flex items-center gap-1.5 text-xs font-mono">
                            <span className="text-emerald-500 font-semibold">{formatTime(firstIn)}</span>
                            <span className="text-muted-foreground">→</span>
                            {lastOut ? (
                              <span className="text-amber-500 font-semibold">{formatTime(lastOut)}</span>
                            ) : r.isActive ? (
                              <Badge className="bg-emerald-500/15 text-emerald-500 border-0 text-[10px] py-0">
                                Active
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </div>
                          {duration && (
                            <Badge variant="secondary" className="text-[10px] font-mono">
                              {duration}
                            </Badge>
                          )}
                        </div>
                      </div>

                      {/* Nested Sessions Accordion */}
                      {hasMultiSessions && isDayExpanded && (
                        <div className="mt-2.5 ml-11 p-3 bg-secondary/30 rounded-lg border border-border/50 space-y-1.5 animate-in fade-in duration-150">
                          <div className="flex items-center justify-between text-[11px] font-mono text-muted-foreground pb-1 border-b border-border/40">
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3 text-primary" /> Sessions Breakdown
                            </span>
                            <span>Total Active: <strong className="text-foreground">{duration}</strong></span>
                          </div>
                          <div className="space-y-1">
                            {r.sessions?.map((s, sIdx) => (
                              <div
                                key={s.id || sIdx}
                                className="flex items-center justify-between text-xs font-mono py-1"
                              >
                                <div className="flex items-center gap-2">
                                  <span className="text-muted-foreground text-[10px]">#{sIdx + 1}</span>
                                  <span className="text-emerald-500 font-semibold">{formatTime(s.checkIn)}</span>
                                  <span className="text-muted-foreground">→</span>
                                  {s.checkOut ? (
                                    <span className="text-amber-500 font-semibold">{formatTime(s.checkOut)}</span>
                                  ) : (
                                    <Badge className="bg-emerald-500/15 text-emerald-500 border-0 text-[9px] py-0">
                                      Active
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  {s.duration && (
                                    <Badge variant="secondary" className="text-[10px] font-mono">
                                      {s.duration}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }

                // Raw Punch View
                const checkInTime = r.checkIn ? formatTime(r.checkIn) : null;
                const checkOutTime = r.checkOut ? formatTime(r.checkOut) : null;
                const punchDuration = calcDuration(r.checkIn, r.checkOut);

                return (
                  <div
                    key={r.id || i}
                    className="flex items-center justify-between py-3 border-b border-border last:border-0"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <CalendarCheck className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{dateLabel}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          {r.branchName && (
                            <p className="text-xs text-muted-foreground">
                              {r.branchName}
                            </p>
                          )}
                          {r.source && r.source.startsWith("biometric") && (
                            <Badge className="bg-blue-500/15 text-blue-400 border-0 text-[9px] font-mono py-0 flex items-center gap-0.5">
                              <Fingerprint className="h-2.5 w-2.5" /> Bio
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right flex flex-col items-end gap-0.5">
                      <div className="flex items-center gap-1.5 text-xs font-mono">
                        <span className="text-emerald-500 font-semibold">{checkInTime || "—"}</span>
                        <span className="text-muted-foreground">→</span>
                        {checkOutTime ? (
                          <span className="text-amber-500 font-semibold">{checkOutTime}</span>
                        ) : checkInTime ? (
                          <Badge className="bg-emerald-500/15 text-emerald-500 border-0 text-[9px] py-0">
                            Active
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </div>
                      {punchDuration && (
                        <Badge variant="secondary" className="text-[10px] font-mono">
                          {punchDuration}
                        </Badge>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Floating Action Button */}
      <button
        onClick={() => setScannerOpen(true)}
        className="fixed bottom-24 right-6 lg:bottom-10 lg:right-10 z-40 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/20 hover:bg-primary/90 hover:scale-105 active:scale-95 transition-all duration-200 border border-primary/20 flex items-center justify-center"
        title="Scan QR Code to Check In"
        data-testid="attendance-scan-fab"
      >
        <QrCode className="h-6 w-6" />
      </button>

      {/* QR Scanner Dialog */}
      <Dialog open={scannerOpen} onOpenChange={setScannerOpen}>
        <DialogContent className="max-w-md w-[90%] rounded-lg border border-border bg-card p-6 shadow-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold">
              Scan Gym QR Code
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground mt-1">
              Position the gym entrance QR code within the scanner viewport to
              check in or out.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center mt-4">
            <div
              id="member-qr-reader"
              className="w-full aspect-square overflow-hidden rounded-md bg-black relative border border-border"
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Check-in Result Dialog */}
      <Dialog open={resultDialogOpen} onOpenChange={setResultDialogOpen}>
        <DialogContent className="max-w-md w-[90%] rounded-lg border border-border bg-card p-6 shadow-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold text-center">
              {resultDialogData.loading ? "Processing..." : resultDialogData.title}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center py-4 space-y-4">
            {resultDialogData.loading ? (
              <div className="flex flex-col items-center space-y-2">
                <Loader2 className="h-12 w-12 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">{resultDialogData.message}</p>
              </div>
            ) : (
              <>
                {resultDialogData.success ? (
                  resultDialogData.warning ? (
                    <AlertTriangle className="h-16 w-16 text-yellow-500 animate-bounce" />
                  ) : (
                    <CheckCircle2 className="h-16 w-16 text-emerald-500 animate-in zoom-in-50 duration-300" />
                  )
                ) : (
                  <XCircle className="h-16 w-16 text-destructive animate-in zoom-in-50 duration-300" />
                )}
                
                <div className="text-center space-y-2 w-full">
                  <p className="text-base font-semibold text-foreground">
                    {resultDialogData.message}
                  </p>
                  
                  {resultDialogData.warning && (
                    <div className="p-3 rounded-md bg-yellow-500/10 border border-yellow-500/30 text-yellow-600 dark:text-yellow-400 text-sm max-w-sm mx-auto">
                      <p className="font-bold flex items-center justify-center gap-1.5">
                        <AlertTriangle className="h-4 w-4" />
                        {resultDialogData.warning === "non_member" ? "No Active Membership" : "Membership Expired"}
                      </p>
                      <p className="text-xs mt-1 text-muted-foreground">
                        {resultDialogData.warning === "non_member" 
                          ? "You do not have an assigned membership. Please contact gym administration."
                          : "Your membership has expired. Please renew to continue without warnings."}
                      </p>
                    </div>
                  )}

                  {resultDialogData.success && resultDialogData.action === "checkin" && resultDialogData.motivationalMessage && (
                    <div className="mt-4 p-4 rounded-md bg-primary/5 border border-primary/10 italic text-muted-foreground text-sm font-medium">
                      "{resultDialogData.motivationalMessage}"
                    </div>
                  )}
                </div>

                <div className="w-full pt-4 space-y-2">
                  {resultDialogData.success && resultDialogData.action === "checkin" && (
                    <button
                      onClick={() => {
                        setResultDialogOpen(false);
                        navigate("/member/workouts");
                      }}
                      className="w-full py-2.5 px-4 rounded bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-medium text-sm flex items-center justify-center gap-2"
                    >
                      Start Today's Workout
                    </button>
                  )}
                  <button
                    onClick={() => setResultDialogOpen(false)}
                    className={`w-full py-2 px-4 rounded transition-colors font-medium text-sm ${
                      resultDialogData.success && resultDialogData.action === "checkin"
                        ? "bg-secondary text-secondary-foreground hover:bg-secondary/80 border border-border"
                        : "bg-primary text-primary-foreground hover:bg-primary/90"
                    }`}
                  >
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
